import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { contractTypesAPI } from '../services/api'
import type { ContractType } from '../types'

export default function ContractTypes() {
  const { t } = useTranslation()
  const [types, setTypes] = useState<ContractType[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<ContractType>>({})
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    contractTypesAPI.list()
      .then((resp) => setTypes(resp.data.contract_types || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    try {
      if (editing.id) {
        await contractTypesAPI.update(editing.id, editing)
      } else {
        await contractTypesAPI.create(editing)
      }
      const resp = await contractTypesAPI.list()
      setTypes(resp.data.contract_types || [])
      setShowForm(false)
      setEditing({})
    } catch { alert('Error al guardar') }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar tipo?')) return
    try {
      await contractTypesAPI.delete(id)
      setTypes((prev) => prev.filter((t) => t.id !== id))
    } catch { alert('Error al eliminar') }
  }

  const startEdit = (ct: ContractType) => {
    setEditing(ct)
    setShowForm(true)
  }

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-tags me-2"></i>{t('contract_type.title')}</h1>
        <button className="btn btn-primary" onClick={() => { setEditing({ name: '', slug: '' }); setShowForm(true) }}>
          <i className="bi bi-plus-lg me-1"></i> Nuevo tipo
        </button>
      </div>

      {showForm && (
        <div className="card mb-3">
          <div className="card-header">{editing.id ? 'Editar tipo' : 'Nuevo tipo'}</div>
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Nombre</label>
                <input className="form-control" value={editing.name || ''}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Slug</label>
                <input className="form-control" value={editing.slug || ''}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Icono</label>
                <input className="form-control" value={editing.icon || 'bi-file-text'}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Orden</label>
                <input className="form-control" type="number" value={editing.sort_order || 0}
                  onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="col-12">
                <label className="form-label">Descripción</label>
                <textarea className="form-control" rows={2} value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" onClick={handleSave}>Guardar</button>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-tags display-4 d-block mb-2"></i>
              <p>No hay tipos de contrato</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Icono</th>
                    <th>Nombre</th>
                    <th>Slug</th>
                    <th>Orden</th>
                    <th>Activo</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((ct) => (
                    <tr key={ct.id}>
                      <td><i className={`bi ${ct.icon || 'bi-file-text'} fs-5`}></i></td>
                      <td className="fw-semibold">{ct.name}</td>
                      <td><code>{ct.slug}</code></td>
                      <td>{ct.sort_order}</td>
                      <td>
                        <span className={`badge bg-${ct.is_active ? 'success' : 'secondary'}`}>
                          {ct.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(ct)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(ct.id)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
